import { useState } from "react";
import {
  PieChart,
  PieInnerText,
  StackedGroupBarChart,
  Tabs,
} from "@jana-technology/platform-ui";

import { InterventionCardHeading } from "components/headings";
import { SM_BAR_CHART_SIZE, XS_BAR_CHART_SIZE } from "constants/charts";
import { useGlobalFiltersContext } from "contexts/filters";
import { xAccessor, x1Accessor } from "helpers/charts";
import { useTopRiskWellsByThreatIntervention } from "hooks/queries";
import { returnTopRiskWellsChartTitle } from "hooks/utils";

import { ChartCard } from "./ChartCard";
import { PaginatedChart } from "./PaginatedChart";

export const TopRiskWellsByThreatIntervention = () => {
  const { hierarchyIdsOverview } = useGlobalFiltersContext();

  const topRiskResult = useTopRiskWellsByThreatIntervention({
    variables: { hierarchyIds: hierarchyIdsOverview },
  });

  const [activeTab, setActiveTab] = useState<
    "riskByThreatsData" | "riskByConsequenceData" | "riskByOutcomeData"
  >("riskByThreatsData");

  const chartSize =
    window.innerWidth <= 1600 ? XS_BAR_CHART_SIZE : SM_BAR_CHART_SIZE;

  return (
    <ChartCard
      loading={topRiskResult.loading}
      loaderClassName="h-[700px] w-[1204px]"
      heading={
        <InterventionCardHeading
          heading={returnTopRiskWellsChartTitle(activeTab)}
        >
          <Tabs
            kind="secondary"
            onValueChange={setActiveTab as (value: string) => void}
            items={[
              { label: "Threat", id: "riskByThreatsData" },
              { label: "Consequence", id: "riskByConsequenceData" },
              { label: "Outcome Scenario", id: "riskByOutcomeData" },
            ]}
          />
        </InterventionCardHeading>
      }
    >
      <div className="flex justify-between">
        <PaginatedChart
          groupSize={2}
          data={topRiskResult[activeTab]}
          annotationsValues={topRiskResult.topWellsTotals}
        >
          <StackedGroupBarChart
            barWidth={32}
            yDomainPadding={1.05}
            height={chartSize.height}
            width={chartSize.width}
            yMaxStackVal={topRiskResult.yMaxStackVal}
            data={topRiskResult[activeTab]}
            dataKeys={topRiskResult[`${activeTab}Keys`]}
            referenceLines={topRiskResult.topWellsMedianLines}
            annotationsValues={topRiskResult.topWellsTotals}
            xAccessor={xAccessor}
            x1Accessor={x1Accessor}
            chartName={returnTopRiskWellsChartTitle(activeTab)}
            yAxisLabel="Risk"
            xAxisLabel="Wells"
          />
        </PaginatedChart>
        <div className="flex items-center">
          <PieChart
            className="ml-6"
            title="System Risk Breakdown"
            pies={topRiskResult.topWellsPieChartData}
          >
            {topRiskResult.topWellsPieChartInnerText.map(
              (pieInnerText, ind) => (
                <g
                  key={pieInnerText.value + ind}
                  transform={`translate(0, ${ind ? ind * 22 : -26})`}
                >
                  <PieInnerText
                    label={pieInnerText.label}
                    value={pieInnerText.value}
                  />
                </g>
              ),
            )}
          </PieChart>
        </div>
      </div>
    </ChartCard>
  );
};

export default TopRiskWellsByThreatIntervention;
